import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Injector,
  afterNextRender,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
  MatOption,
} from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Translator } from '@vectronic/util';

@Component({
  selector: 'table-datatable-autocomplete-input',
  templateUrl: './datatable-autocomplete-input.component.html',
  styleUrls: ['./datatable-autocomplete-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatOption,
    MatTooltipModule,
  ],
})
export class DatatableAutocompleteInputComponent {
  value = input<string>('');
  options = input<string[]>([]);
  editable = input<boolean>(false);
  placeholder = input<string>('Enter value…');
  emptySymbol = input<string>('—');
  trim = input<number | undefined>(undefined);

  valueChange = output<string>();

  protected readonly isEditing = signal(false);
  protected readonly draft = signal('');
  protected readonly inputRef = viewChild<ElementRef<HTMLInputElement>>('inputRef');

  private readonly injector = inject(Injector);

  protected readonly isEmpty = computed(() => !this.value());
  protected readonly isTrimmed = computed(() => {
    const trim = this.trim();
    return !!trim && (this.value()?.length ?? 0) > trim;
  });
  protected readonly displayValue = computed(() => {
    const value = this.value() ?? '';
    const trim = this.trim();
    return this.isTrimmed() ? Translator.trimString(value, trim!) : value;
  });
  protected readonly filteredOptions = computed(() => {
    const search = this.draft().toLowerCase();
    return this.options().filter((option) => option.toLowerCase().includes(search));
  });

  protected onInput(event: Event): void {
    this.draft.set((event.target as HTMLInputElement).value);
  }

  protected onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    this.draft.set(event.option.value);
  }

  protected submitAfterAutocompleteSelection(): void {
    // Material applies the active option after the keydown event; submit after that update.
    setTimeout(() => this.submit());
  }

  protected startEdit(): void {
    this.draft.set(this.value() ?? '');
    this.isEditing.set(true);
    afterNextRender(() => this.inputRef()?.nativeElement.focus(), { injector: this.injector });
  }

  protected cancelEdit(): void {
    this.isEditing.set(false);
    this.draft.set('');
  }

  protected submit(): void {
    this.valueChange.emit(this.draft().trim());
    this.isEditing.set(false);
  }
}
